import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useNavigate, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';

import './PersonaPage.css';
import { foodTypes } from './Food/FoodLayout';
import { FetchCountryName } from './Utils/CountryUtility';
import { IsHtml } from './Utils/Utility';

type LatestFoodPost = {
  representative_id?: number;
  id?: number;
  iso?: string;
  region?: string;
  dish?: string;
  restaurant?: string;
  description?: string;
  images?: string[];
  attributions?: string[];
  countries?: Array<{ iso?: string; region?: string }>;
};

type FoodTypePost = {
  id: number;
  iso: string;
  region?: string;
  dish?: string;
  restaurant?: string;
  description?: string;
  images?: string[];
  attributions?: string[];
};

const backendUrl = process.env.REACT_APP_BACKEND_URL;
const FOODTYPE_BATCH_SIZE = 5;
const MIN_FETCH_INTERVAL_MS = 2000;
const BOTTOM_TOLERANCE_PX = 2;

const normalizeLatestPosts = (value: unknown): FoodTypePost[] => {
  if (!Array.isArray(value)) return [];

  return value
    .map((post): FoodTypePost | null => {
      const item = post as LatestFoodPost;
      const id = typeof item.representative_id === 'number' ? item.representative_id : item.id;
      if (typeof id !== 'number') return null;

      const fallbackCountry = Array.isArray(item.countries) ? item.countries[0] : undefined;
      const iso = item.iso || fallbackCountry?.iso;
      if (!iso) return null;

      return {
        id,
        iso,
        region: item.region || fallbackCountry?.region,
        dish: item.dish,
        restaurant: item.restaurant,
        description: item.description,
        images: Array.isArray(item.images) ? item.images : [],
        attributions: Array.isArray(item.attributions) ? item.attributions : [],
      };
    })
    .filter((post): post is FoodTypePost => post !== null);
};

function FoodType(): JSX.Element {
  const navigate = useNavigate();
  const { foodTypeId } = useParams<{ foodTypeId: string }>();
  const foodType = foodTypes.find((item) => item.id.toLowerCase() === (foodTypeId || '').toLowerCase()) || null;

  const [posts, setPosts] = useState<FoodTypePost[]>([]);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [isLoadQueued, setIsLoadQueued] = useState(false);
  const [hasMorePosts, setHasMorePosts] = useState(true);

  const gridRef = useRef<HTMLDivElement | null>(null);

  const isLoadingMoreRef = useRef(false);
  const hasMorePostsRef = useRef(true);
  const nextPageRef = useRef(1);
  const initialLoadTriggeredRef = useRef(false);
  const lastFoodTypeIdRef = useRef<string | null>(null);
  const pendingScrollToIndexRef = useRef<number | null>(null);
  const lastFetchStartedAtRef = useRef(0);
  const fetchCooldownTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const touchStartYRef = useRef<number | null>(null);

  const isAtGridEnd = useCallback((grid: HTMLDivElement): boolean => {
    return grid.scrollTop + grid.clientHeight >= grid.scrollHeight - BOTTOM_TOLERANCE_PX;
  }, []);

  const fetchLatestPage = useCallback(
    async (page: number) => {
      if (!foodType?.id) {
        return { posts: [] as FoodTypePost[], currentPage: page, totalPages: page };
      }

      const response = await axios.get(`${backendUrl}/worlddata/food/get-food-data-linked/${FOODTYPE_BATCH_SIZE}/`, {
        params: {
          page,
          food_category: foodType.id,
        },
      });

      return {
        posts: normalizeLatestPosts(response.data?.results),
        currentPage: Number(response.data?.current_page || page),
        totalPages: Number(response.data?.total_pages || page),
      };
    },
    [foodType?.id],
  );

  const resetFeedState = useCallback(() => {
    if (fetchCooldownTimerRef.current) {
      clearTimeout(fetchCooldownTimerRef.current);
      fetchCooldownTimerRef.current = null;
    }

    setPosts([]);
    setIsLoadQueued(false);
    setHasMorePosts(true);
    hasMorePostsRef.current = true;
    isLoadingMoreRef.current = false;
    nextPageRef.current = 1;
    pendingScrollToIndexRef.current = null;
    lastFetchStartedAtRef.current = 0;
    touchStartYRef.current = null;
  }, []);

  const loadMorePosts = useCallback(async () => {
    if (!foodType?.id || isLoadingMoreRef.current || !hasMorePostsRef.current) return;

    const now = Date.now();
    const elapsedSinceLastFetch = now - lastFetchStartedAtRef.current;
    if (elapsedSinceLastFetch < MIN_FETCH_INTERVAL_MS) {
      if (!fetchCooldownTimerRef.current) {
        setIsLoadQueued(true);
        const waitTime = MIN_FETCH_INTERVAL_MS - elapsedSinceLastFetch;
        fetchCooldownTimerRef.current = setTimeout(() => {
          fetchCooldownTimerRef.current = null;
          setIsLoadQueued(false);
          void loadMorePosts();
        }, waitTime);
      }
      return;
    }

    setIsLoadQueued(false);
    lastFetchStartedAtRef.current = now;
    isLoadingMoreRef.current = true;
    setIsLoadingMore(true);

    try {
      const pageToLoad = nextPageRef.current;
      const result = await fetchLatestPage(pageToLoad);

      if (result.posts.length > 0) {
        setPosts((previous) => {
          if (previous.length > 0) {
            pendingScrollToIndexRef.current = previous.length;
          }
          return [...previous, ...result.posts];
        });
      } else {
        hasMorePostsRef.current = false;
        setHasMorePosts(false);
      }

      const hasMore = result.currentPage < result.totalPages;
      hasMorePostsRef.current = hasMore;
      setHasMorePosts(hasMore);
      nextPageRef.current = result.currentPage + 1;
    } catch (error) {
      console.error('Error fetching food type data:', error);
      hasMorePostsRef.current = false;
      setHasMorePosts(false);
    } finally {
      isLoadingMoreRef.current = false;
      setIsLoadingMore(false);
    }
  }, [fetchLatestPage, foodType?.id]);

  const triggerLoadFromBoundaryGesture = useCallback(() => {
    const grid = gridRef.current;
    if (!grid) return;

    if (!hasMorePostsRef.current || isLoadingMoreRef.current) return;
    if (!isAtGridEnd(grid)) return;

    void loadMorePosts();
  }, [isAtGridEnd, loadMorePosts]);

  const handleGridWheel = useCallback(
    (event: React.WheelEvent<HTMLDivElement>) => {
      if (event.deltaY <= 0) return;
      triggerLoadFromBoundaryGesture();
    },
    [triggerLoadFromBoundaryGesture],
  );

  const handleGridTouchStart = useCallback((event: React.TouchEvent<HTMLDivElement>) => {
    touchStartYRef.current = event.touches[0]?.clientY ?? null;
  }, []);

  const handleGridTouchEnd = useCallback(
    (event: React.TouchEvent<HTMLDivElement>) => {
      if (touchStartYRef.current === null) return;

      const endY = event.changedTouches[0]?.clientY;
      if (typeof endY !== 'number') {
        touchStartYRef.current = null;
        return;
      }

      const swipeDistance = touchStartYRef.current - endY;
      touchStartYRef.current = null;

      if (swipeDistance <= 10) return;
      triggerLoadFromBoundaryGesture();
    },
    [triggerLoadFromBoundaryGesture],
  );

  useEffect(() => {
    if (!foodType?.id) return;

    const foodTypeChanged = lastFoodTypeIdRef.current !== foodType.id;
    if (foodTypeChanged) {
      lastFoodTypeIdRef.current = foodType.id;
      initialLoadTriggeredRef.current = false;
      resetFeedState();
    }

    if (initialLoadTriggeredRef.current) return;
    initialLoadTriggeredRef.current = true;
    void loadMorePosts();
  }, [foodType?.id, loadMorePosts, resetFeedState]);

  useEffect(() => {
    return () => {
      if (fetchCooldownTimerRef.current) {
        clearTimeout(fetchCooldownTimerRef.current);
        fetchCooldownTimerRef.current = null;
      }
      setIsLoadQueued(false);
    };
  }, []);

  useEffect(() => {
    if (isLoadingMore) return;

    const targetIndex = pendingScrollToIndexRef.current;
    if (targetIndex === null) return;

    const grid = gridRef.current;
    if (!grid) {
      pendingScrollToIndexRef.current = null;
      return;
    }

    const cards = grid.querySelectorAll<HTMLElement>('.PersonaCard');
    const targetCard = cards[targetIndex];
    if (targetCard) {
      grid.scrollTo({ top: targetCard.offsetTop, behavior: 'smooth' });
    }

    pendingScrollToIndexRef.current = null;
  }, [isLoadingMore, posts.length]);

  const toPlainText = (value: unknown): string =>
    String(value || '')
      .replace(/<[^>]*>/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();

  const openPost = async (post: FoodTypePost): Promise<void> => {
    try {
      const countryName = await FetchCountryName(post.iso);
      const cursorResponse = await axios.get(`${backendUrl}/worlddata/food/get-cursor-for-item/${post.id}/9/?iso=${post.iso}`);
      const cursor = cursorResponse.data.cursor;
      const cursorToken = cursorResponse.data.cursor_token || '';

      navigate(`/traveltopics/${countryName}/topics/food`, {
        state: {
          selectedItemId: post.id,
          selectedItemCursor: cursor,
          selectedItemCursorToken: cursorToken,
          category: foodType?.id || '',
        },
      });
    } catch (error) {
      console.error('Error navigating to food post from food type page:', error);
    }
  };

  if (!foodType) {
    return (
      <div className="PersonaPage">
        <h1>Food type not found</h1>
      </div>
    );
  }

  const canonicalUrl = `https://livingoutofabackpack.com/foodtypes/${encodeURIComponent(foodType.id)}`;
  const seoTitle = `${foodType.label} Food Reels | Living Out Of A Backpack`;
  const seoDescription = `Swipe through ${foodType.label.toLowerCase()} food reels across countries.`;

  return (
    <div className="PersonaPage">
      <Helmet>
        <title>{seoTitle}</title>
        <link rel="canonical" href={canonicalUrl} />
        <meta name="robots" content="index, follow" />
        <meta name="description" content={seoDescription} />
        <meta property="og:title" content={seoTitle} />
        <meta property="og:description" content={seoDescription} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={canonicalUrl} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={seoTitle} />
        <meta name="twitter:description" content={seoDescription} />
      </Helmet>

      <div className="PersonaHeaderBlock">
        <h1>
          {foodType.label} Reels {foodType.emoji}
        </h1>
        <h2>{foodType.explanation} • Swipe to explore posts.</h2>
        <p className="PersonaScrollHint">Swipe or scroll up for the next reel.</p>
      </div>

      <div className="PersonaGrid" ref={gridRef} onWheel={handleGridWheel} onTouchStart={handleGridTouchStart} onTouchEnd={handleGridTouchEnd}>
        {(isLoadingMore || isLoadQueued) && posts.length > 0 ? (
          <div className="PersonaFeedStatus PersonaFeedLoading PersonaFeedLoadingOverlay" aria-live="polite">
            <span className="PersonaLoadingSpinner" aria-hidden="true" />
            <span>Loading more posts…</span>
          </div>
        ) : null}

        {posts.length > 0 ? (
          <>
            {posts.map((post, index) => (
              <article
                key={`${post.id}-${index}`}
                className="PersonaCard"
                role="button"
                tabIndex={0}
                aria-label={`Open ${post.dish || post.restaurant || `Post ${post.id}`} in food page`}
                onClick={() => openPost(post)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    openPost(post);
                  }
                }}
              >
                <div className="PersonaImageWrap">
                  <img src={`${backendUrl}/django-dynamic/pictures/${post.images?.[0]}`} alt={post.dish || post.restaurant || 'Food post'} />

                  <div className="PersonaCountryBadge">
                    <img src={`https://flagcdn.com/w320/${String(post.iso || '').toLowerCase()}.png`} alt={`${post.iso} flag`} />
                    <span>{post.iso}</span>
                  </div>

                  {post.attributions?.[0] && (
                    <div className="attribution">
                      <div className="attribution-content">
                        {IsHtml(post.attributions[0]) ? <div dangerouslySetInnerHTML={{ __html: post.attributions[0] }} /> : <span>{post.attributions[0]}</span>}
                      </div>
                    </div>
                  )}

                  <div className="PersonaReelOverlay">
                    <div className="PersonaCardTitle">{post.dish || post.restaurant}</div>
                    <div className="PersonaCardMeta">
                      {post.region} • {post.iso}
                    </div>
                    <div className="PersonaCardDescription">{toPlainText(post.description) || 'No description available.'}</div>
                  </div>

                  <div className="PersonaReelActions">
                    <button
                      type="button"
                      className="PersonaOpenLink"
                      onClick={(e) => {
                        e.stopPropagation();
                        openPost(post);
                      }}
                    >
                      Open
                    </button>
                  </div>
                </div>
              </article>
            ))}
            {!hasMorePosts ? <div className="PersonaFeedStatus">You reached the end of this food type feed.</div> : null}
          </>
        ) : (
          <div className="PersonaEmpty">{isLoadingMore ? 'Loading posts…' : 'No posts available for this food type yet.'}</div>
        )}
      </div>
    </div>
  );
}

export default FoodType;
